/**
 * Quick capture: log a ticket without leaving the page. Opened by C, the rail, the account page's
 * header button and the lookup's "Start a ticket" on a recognised caller. An overlay, never a
 * route; Escape restores the page (a second Escape when something was typed).
 *
 * The caller comes from the account preset or from the phone, through the same resolver and
 * shapes as the lookup. Caller name and email follow the rulebook's prefill hygiene (the
 * `prefillStep` reducer): a prefill is cleared when the number changes, what the rep typed is
 * kept, and an untouched prefill never reaches the contact.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { type TicketCreateInput } from '@lsc/contract';
import {
  activeVocabNames,
  allowedSuggestion,
  changedFromPrefill,
  EMPTY_PREFILL,
  prefillStep,
  ROUTED_TO,
  suggestCategory,
  WAITING_ON,
  digitsOf,
  type CallerResolution,
  type PrefillEvent,
  type PrefillState,
} from '@lsc/domain';
import { useEffect, useReducer, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { WAITING_LABEL } from '../../lib/format';
import { lookupPlan } from '../../lib/lookup-plan';
import { DRAFT_ASK_MS, DRAFT_WORDS, draftStatus, fillFromDraft, nextDraftPoll } from '../../lib/call-draft-fill';
import { accountQuery, callDraftQuery, recentCallQuery, resolveQuery, vocabQuery } from '../../lib/queries';
import { describeRpcError, rpc } from '../../lib/rpc';
import { Resolution, resolutionAccounts } from '../lookup/resolution';
import { Button } from '../ui/button';
import { liveProfileId } from '../../lib/live';
import { captureLogged, closeCapture, dismissLogged, useCaptureState, type CapturePreset } from './capture-store';

export function QuickCapture() {
  const { open, session, preset, lastLogged } = useCaptureState();
  return (
    <>
      {open && <CaptureForm key={session} preset={preset} />}
      {lastLogged && <Logged {...lastLogged} />}
    </>
  );
}

/** Ten seconds to reach Open (UAT 2026-10-22: six went by too fast); it holds while the pointer is on it. */
const LOGGED_MS = 10_000;

function Logged({ ticketId, ticketNumber, at }: { ticketId: string; ticketNumber: string; at: number }) {
  const [held, setHeld] = useState(false);
  useEffect(() => {
    if (held) return undefined;
    const t = setTimeout(dismissLogged, LOGGED_MS);
    return () => {
      clearTimeout(t);
    };
  }, [at, held]);
  return (
    <div
      role="status"
      onMouseEnter={() => { setHeld(true); }}
      onMouseLeave={() => { setHeld(false); }}
      onFocus={() => { setHeld(true); }}
      onBlur={() => { setHeld(false); }}
      className="fixed right-4 bottom-4 z-50 flex items-center gap-3 rounded-panel border border-line bg-surface px-4 py-2 text-body text-ink shadow-lg">
      Logged <span className="mono">{ticketNumber}</span>
      <Link to="/tickets/$ticketId" params={{ ticketId }} className="text-info hover:underline" onClick={dismissLogged}>
        Open
      </Link>
    </div>
  );
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => {
      setV(value);
    }, ms);
    return () => {
      clearTimeout(t);
    };
  }, [value, ms]);
  return v;
}

const isEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

function CaptureForm({ preset }: { preset: CapturePreset }) {
  const qc = useQueryClient();
  const presetAccount = preset.accountId ?? null;
  const [phone, setPhone] = useState(preset.phone ?? '');
  const [prefill, dispatch] = useReducer((s: PrefillState, e: PrefillEvent) => prefillStep(s, e), EMPTY_PREFILL);
  const [cursor, setCursor] = useState(0);
  /** On a shared line the rep must pick the location; the highlighted row is only a suggestion. */
  const [pickedId, setPickedId] = useState<string | null>(null);
  const savingRef = useRef(false);
  const [company, setCompany] = useState('');
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [product, setProduct] = useState('');
  const [notes, setNotes] = useState('');
  const [routedTo, setRoutedTo] = useState('');
  const [waitingOn, setWaitingOn] = useState('');
  const [assignToSelf, setAssignToSelf] = useState(true);
  const [showResolve, setShowResolve] = useState(false);
  const [resolution, setResolution] = useState('');
  const [discard, setDiscard] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const first = useRef<HTMLInputElement>(null);
  const resolutionRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    first.current?.focus();
  }, []);

  const account = useQuery({ ...accountQuery(presetAccount ?? ''), enabled: presetAccount !== null });
  const debounced = useDebounced(phone.trim(), 150);
  const resolving = presetAccount === null && lookupPlan(debounced).resolving;
  const resolve = useQuery({ ...resolveQuery(debounced), enabled: resolving });
  const r: CallerResolution | undefined = resolving ? resolve.data : undefined;
  const locations = r ? resolutionAccounts(r) : [];
  const highlighted = locations[Math.min(cursor, Math.max(locations.length - 1, 0))] ?? null;
  const shared = r?.status === 'many';
  const picked = r?.status === 'one' ? r.match : shared ? (locations.find((l) => l.accountId === pickedId) ?? null) : null;
  const draft = r?.status === 'none' ? r.draft : null;
  const draftId = preset.draftId ?? draft?.draftId ?? null;
  const accountId = presetAccount ?? picked?.accountId ?? null;

  // A match (or the highlighted shared-line row, or a recognised draft) prefills the caller.
  const prefillFrom = picked ?? highlighted;
  const matchName = prefillFrom?.contactName ?? draft?.contactName ?? null;
  const matchKey = `${prefillFrom?.accountId ?? ''}|${draft?.draftId ?? ''}|${matchName ?? ''}`;
  useEffect(() => {
    if (prefillFrom || draft) dispatch({ type: 'match', name: matchName, email: null });
  }, [matchKey]);

  // The pickers come from Settings (support.ticket_vocab); hidden values are not offered, and a
  // rule suggestion for a hidden category is no suggestion.
  const vocab = useQuery(vocabQuery());
  const categories = vocab.data ? activeVocabNames(vocab.data.categories, 'category') : [];
  const products = vocab.data ? activeVocabNames(vocab.data.products, 'product') : [];
  // Logged from a call: fill Subject, Notes and the resolve tick from Dialpad's recap, asking every
  // 3 s while it is on its way (it lands a median 16 s after hangup). What the rep touched stays.
  // Opened without a call (the account page's button, C): the call this account just had, with no
  // ticket yet, is the one it is about (2026-11-02: a ticket logged 8 minutes after the call by the
  // plain button never linked). Linked on its own; "Not this call" undoes it.
  const [notThisCall, setNotThisCall] = useState(false);
  const recentFor = preset.rootCallId ? null : accountId ? { accountId } : digitsOf(phone).length >= 10 ? { phone: phone.trim() } : null;
  const recent = useQuery({ ...recentCallQuery(recentFor ?? {}), enabled: recentFor !== null && !notThisCall });
  const linkedCall = !preset.rootCallId && !notThisCall ? (recent.data?.call ?? null) : null;
  const rootCallId = preset.rootCallId ?? linkedCall?.rootCallId ?? null;
  // The call's answering rep, when it is someone else: the ticket is theirs by default (UX audit
  // 2026-11-03; a lead logging the day's calls was making themself the holder of each one).
  const me = liveProfileId();
  const answeredBy = preset.answeredBy ?? (linkedCall?.answeredByProfileId && linkedCall.answeredByName ? { profileId: linkedCall.answeredByProfileId, name: linkedCall.answeredByName } : null);
  const otherRep = answeredBy && answeredBy.profileId !== me ? answeredBy : null;
  const [assignToRep, setAssignToRep] = useState<boolean | null>(null);
  const toRep = otherRep !== null && (assignToRep ?? true);
  const [openedAt] = useState(() => Date.now());
  const touched = useRef({ subject: false, notes: false, resolve: false });
  const callDraft = useQuery({
    ...callDraftQuery(rootCallId ?? ''),
    enabled: rootCallId !== null,
    refetchInterval: (q) => nextDraftPoll(q.state.data, Date.now() - openedAt),
  });
  const [, tick] = useReducer((n: number) => n + 1, 0);
  const drafting = rootCallId === null ? null : draftStatus(callDraft.data, callDraft.isError, Date.now() - openedAt);
  // Category: the rep's pick, else the subject rule, else the recap's (rules over its words, else Dialpad's purpose); a hidden one is no suggestion.
  const suggested = allowedSuggestion(suggestCategory(subject), categories);
  const draftCategory = allowedSuggestion(callDraft.data?.category ?? null, categories);
  const categoryValue = category ?? suggested ?? draftCategory ?? '';
  useEffect(() => {
    // Re-read the status once the 90 s are up, so "Waiting" turns into "no recap" on its own.
    if (drafting !== 'waiting') return undefined;
    const t = setTimeout(tick, Math.max(0, openedAt + DRAFT_ASK_MS - Date.now()) + 50);
    return () => {
      clearTimeout(t);
    };
  }, [drafting, openedAt]);
  useEffect(() => {
    const d = callDraft.data;
    if (!d) return;
    const fill = fillFromDraft({ subject, notes, resolveTicked: showResolve, resolution }, touched.current, d);
    if (fill.subject !== undefined) setSubject(fill.subject);
    if (fill.notes !== undefined) setNotes(fill.notes);
    if (fill.resolveTicked !== undefined) setShowResolve(fill.resolveTicked);
    if (fill.resolution !== undefined) setResolution(fill.resolution);
  }, [callDraft.data]);

  const changed = changedFromPrefill(prefill);
  const dirty = Boolean(subject.trim() || notes.trim() || resolution.trim() || company.trim() || changed.name || changed.email);
  const newCaller = accountId === null && draftId === null && !shared;

  const save = async (resolveNow: boolean) => {
    if (savingRef.current) return;
    if (subject.trim().length < 3) {
      setError('Give it a subject of at least 3 characters.');
      return;
    }
    if (resolveNow && !resolution.trim()) {
      setShowResolve(true);
      setError('Say what fixed it.');
      return;
    }
    const email = prefill.email.trim();
    const input: TicketCreateInput = {
      subject: subject.trim(),
      assignToSelf: toRep ? false : assignToSelf,
      ...(toRep && otherRep ? { assignTo: otherRep.profileId } : {}),
      source: 'phone',
      contactChanged: changed,
      ...(accountId ? { accountId } : {}),
      ...(!accountId && draftId ? { draftId } : {}),
      // A call card knows the number even with the account preset; it is the caller's line.
      ...(phone.trim() && (presetAccount === null || rootCallId) ? { phone: phone.trim() } : {}),
      ...(rootCallId ? { rootCallId } : {}),
      ...(newCaller && company.trim() ? { callerCompany: company.trim() } : {}),
      ...(prefill.name.trim() ? { callerName: prefill.name.trim() } : {}),
      ...(email && isEmail(email) ? { callerEmail: email } : {}),
      ...(categoryValue ? { category: categoryValue } : {}),
      ...(product ? { product } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
      ...(routedTo ? { routedTo: routedTo as (typeof ROUTED_TO)[number] } : {}),
      ...(waitingOn ? { waitingOn: waitingOn as (typeof WAITING_ON)[number] } : {}),
      ...(resolveNow ? { resolveNow: { resolution: resolution.trim() } } : {}),
    };
    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      const out = await rpc.tickets.create(input);
      void qc.invalidateQueries({ queryKey: ['accounts.get'] });
      void qc.invalidateQueries({ queryKey: ['tickets.board'] });
      captureLogged(out.ticketId, out.ticketNumber);
    } catch (err) {
      setError(describeRpcError(err));
      savingRef.current = false;
      setSaving(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (dirty && !discard) setDiscard(true);
      else closeCapture();
      return;
    }
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (e.shiftKey) {
        if (!showResolve) {
          setShowResolve(true);
          setTimeout(() => resolutionRef.current?.focus(), 0);
        } else void save(true);
      } else void save(showResolve && resolution.trim() !== '');
    }
  };

  const onPhoneKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, Math.max(locations.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey && shared && highlighted) {
      e.preventDefault();
      setPickedId(highlighted.accountId);
    }
  };

  const digits = digitsOf(phone).length;

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center bg-canvas/70 pt-[8vh]" onMouseDown={(e) => {
      if (e.target === e.currentTarget && !dirty) closeCapture();
    }}>
      <div role="dialog" aria-modal="true" aria-labelledby="capture-title" onKeyDown={onKeyDown} className="flex max-h-[84vh] w-full max-w-[640px] flex-col overflow-hidden rounded-panel border border-line bg-surface shadow-xl">
        <div className="flex h-12 items-center justify-between border-b border-line px-5">
          <h2 id="capture-title" className="text-title font-medium">
            Log a ticket
          </h2>
          <span className="text-hint text-ink-3">
            <kbd>Ctrl</kbd> <kbd>↵</kbd> log, <kbd>Ctrl</kbd> <kbd>Shift</kbd> <kbd>↵</kbd> log resolved, <kbd>Esc</kbd> cancel
          </span>
        </div>

        <div className="flex flex-col gap-4 overflow-y-auto px-5 py-4">
          {linkedCall && (
            <p className="text-hint text-ink-2">
              {`From the ${callClock(linkedCall.startedAt)} call${linkedCall.label ? ` with ${linkedCall.label}` : ''}${linkedCall.answeredByName ? `, answered by ${linkedCall.answeredByName}` : ''}. `}
              <button
                type="button"
                className="text-info hover:underline"
                onClick={() => {
                  setNotThisCall(true);
                }}
              >
                Not this call
              </button>
            </p>
          )}
          {drafting && (
            <p role="status" className={`text-hint ${drafting === 'filled' ? 'text-info' : 'text-ink-3'}`}>
              {DRAFT_WORDS[drafting]}
            </p>
          )}
          {presetAccount !== null ? (
            <p className="text-body text-ink-2">
              For <span className="font-medium text-ink">{account.data?.label ?? 'this account'}</span>
            </p>
          ) : (
            <div>
              <Field label="Caller phone" id="capture-phone">
                <input
                  id="capture-phone"
                  ref={first}
                  className={INPUT + ' mono'}
                  value={phone}
                  placeholder="(407) 555-0123"
                  autoComplete="off"
                  onKeyDown={onPhoneKey}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    setCursor(0);
                    setPickedId(null);
                    dispatch({ type: 'numberChanged' });
                  }}
                />
              </Field>
              {shared && picked ? (
                <p className="mt-2 text-body text-ink-2">
                  For <span className="font-medium text-ink">{picked.label}</span>
                  {picked.city ? `, ${picked.city}` : ''}{' '}
                  <button type="button" className="text-info hover:underline" onClick={() => { setPickedId(null); }}>
                    change
                  </button>
                </p>
              ) : (
                r && r.status !== 'unparseable' && <Resolution r={r} cursor={cursor} onHover={setCursor} onOpen={(id) => { setPickedId(id); }} />
              )}
              {shared && !picked && <p className="mt-1 text-hint text-ink-3">Pick the location the caller is at, or log it on the number alone.</p>}
              {digits > 0 && digits < 7 && <p className="mt-1 text-hint text-ink-3">Keep typing the number.</p>}
            </div>
          )}

          {newCaller && presetAccount === null && digits >= 7 && (
            <Field label="Company" id="capture-company">
              <input id="capture-company" className={INPUT} value={company} placeholder="Store name, as the caller says it" onChange={(e) => { setCompany(e.target.value); }} />
            </Field>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Caller name" id="capture-name">
              <input id="capture-name" className={INPUT} value={prefill.name} onChange={(e) => { dispatch({ type: 'typed', field: 'name', value: e.target.value }); }} />
            </Field>
            <Field label="Caller email" id="capture-email">
              <input id="capture-email" type="email" className={INPUT} value={prefill.email} onChange={(e) => { dispatch({ type: 'typed', field: 'email', value: e.target.value }); }} />
            </Field>
          </div>

          <Field label="Subject" id="capture-subject">
            <input
              id="capture-subject"
              ref={presetAccount !== null ? first : undefined}
              className={INPUT}
              value={subject}
              maxLength={140}
              placeholder="What is the call about"
              onChange={(e) => {
                touched.current.subject = true;
                setSubject(e.target.value);
                setDiscard(false);
              }}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Category" id="capture-category">
              <select id="capture-category" className={INPUT} value={categoryValue} onChange={(e) => { setCategory(e.target.value); }}>
                <option value="">No category</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Product" id="capture-product">
              <select id="capture-product" className={INPUT} value={product} onChange={(e) => { setProduct(e.target.value); }}>
                <option value="">Not product specific</option>
                {products.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Notes" id="capture-notes">
            <textarea id="capture-notes" rows={3} className={INPUT + ' h-auto py-2'} value={notes} onChange={(e) => { touched.current.notes = true; setNotes(e.target.value); }} />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Route to" id="capture-route">
              <select id="capture-route" className={INPUT} value={routedTo} onChange={(e) => { setRoutedTo(e.target.value); }}>
                <option value="">Stays with support</option>
                {ROUTED_TO.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Waiting on" id="capture-waiting">
              <select id="capture-waiting" className={INPUT} value={waitingOn} onChange={(e) => { setWaitingOn(e.target.value); }}>
                <option value="">Nothing</option>
                {WAITING_ON.map((w) => (
                  <option key={w} value={w}>
                    {WAITING_LABEL[w] ?? w}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {showResolve && (
            <Field label="Resolution" id="capture-resolution">
              <input id="capture-resolution" ref={resolutionRef} className={INPUT} value={resolution} placeholder="What fixed it" onChange={(e) => { touched.current.resolve = true; setResolution(e.target.value); }} />
            </Field>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t border-line px-5 py-3">
          {otherRep && (
            <label className="flex items-center gap-2 text-body text-ink-2">
              <input
                type="checkbox"
                checked={toRep}
                onChange={(e) => {
                  setAssignToRep(e.target.checked);
                  if (e.target.checked) setAssignToSelf(false);
                }}
              />
              {`Assign to ${otherRep.name}`}
            </label>
          )}
          <label className="flex items-center gap-2 text-body text-ink-2">
            <input
              type="checkbox"
              checked={!toRep && assignToSelf}
              onChange={(e) => {
                setAssignToSelf(e.target.checked);
                if (e.target.checked) setAssignToRep(false);
              }}
            />{' '}
            Assign to me
          </label>
          <label className="flex items-center gap-2 text-body text-ink-2">
            <input type="checkbox" checked={showResolve} onChange={(e) => { touched.current.resolve = true; setShowResolve(e.target.checked); }} /> Resolved on the call
          </label>
          {discard && <span className="text-body text-attention">Discard this ticket? Press Escape again.</span>}
          {error && (
            <span role="alert" className="text-body text-alert">
              {error}
            </span>
          )}
          <div className="ml-auto flex gap-2">
            <Button variant="ghost" onClick={() => { if (dirty && !discard) setDiscard(true); else closeCapture(); }}>
              Cancel
            </Button>
            <Button variant="primary" disabled={saving} onClick={() => { void save(showResolve && resolution.trim() !== ''); }}>
              Log it
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** "9:19 AM", the rep's own clock. */
function callClock(iso: string | null): string {
  return iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : 'recent';
}

const INPUT = 'h-9 w-full rounded-control border border-line bg-surface-2 px-3 text-body text-ink placeholder:text-ink-3 focus:border-info focus-visible:outline-none';

function Field({ label, id, children }: { label: string; id: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-hint text-ink-3">
        {label}
      </label>
      {children}
    </div>
  );
}
